/**
 * useRepresentanteForm - Hook for RepresentanteFormModal
 *
 * Manages:
 * - Form state and initialization
 * - CPF/CNPJ and telefone input masks and validation
 * - Submit handler (create/update)
 * - Hospital dual-list state and operations (link/unlink)
 * - Filtered lists for dual-list UI
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { representantesService, Representante, RepresentanteCreate, HospitalAssociation } from '../services/representantesService';
import clienteService from '../services/clienteService';
import { msg } from '../utils/messages';
import { mascaraCPF, mascaraCNPJ, validarCPF, validarCNPJ, limparDocumento, mascaraTelefone, validarTelefone } from '../lib/utils';
import { cnpjMatch } from '../utils/cnpjMatch';  // Story 2.38
import { useConfirm } from './useConfirm';

export interface HospitalItem {
  id: number;
  nome: string;
  codigo: string;
  cidade: string;
  estado: string;
  nome_fantasia?: string;
}

const validateEmail = (email: string): boolean => {
  const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return regex.test(email);
};

interface UseRepresentanteFormProps {
  representante: Representante | null;
  clienteId?: number;
  onClose: () => void;
  onSave: () => void;
}

export function useRepresentanteForm({
  representante,
  clienteId,
  onClose,
  onSave,
}: UseRepresentanteFormProps) {
  const { confirm, ConfirmDialog } = useConfirm();

  const [formData, setFormData] = useState<RepresentanteCreate>({
    nome: '',
    cpf_cnpj: '',
    telefone: '',
    email: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Dual-list state
  const [availableHospitais, setAvailableHospitais] = useState<HospitalItem[]>([]);
  const [associations, setAssociations] = useState<HospitalAssociation[]>([]);
  const [loadingHospitais, setLoadingHospitais] = useState(false);
  const [selectedAvailable, setSelectedAvailable] = useState<number[]>([]);
  const [selectedLinked, setSelectedLinked] = useState<number[]>([]);
  const [searchLeft, setSearchLeft] = useState('');
  const [searchRight, setSearchRight] = useState('');
  const [linking, setLinking] = useState(false);
  const [unlinking, setUnlinking] = useState(false);

  // Initialize form data from representante
  useEffect(() => {
    if (representante) {
      setFormData({
        nome: representante.nome,
        cpf_cnpj: representante.cpf_cnpj,
        telefone: representante.telefone,
        email: representante.email,
      });
    }
  }, [representante]);

  // Load hospitais for dual-list
  const fetchHospitais = useCallback(async () => {
    if (!clienteId) return;
    try {
      setLoadingHospitais(true);
      const hospitaisData = await clienteService.getClienteHospitais(clienteId);
      const formattedHospitais: HospitalItem[] = Array.isArray(hospitaisData)
        ? hospitaisData.map((hospital: any) => ({
            id: hospital.hospital_id,
            nome: hospital.hospital_nome,
            codigo: hospital.hospital_codigo,
            cidade: hospital.hospital_cidade,
            estado: hospital.hospital_estado,
          }))
        : [];
      setAvailableHospitais(formattedHospitais);

      if (representante) {
        try {
          const hospitaisVinculados = await representantesService.getHospitaisAssociados(representante.id);
          setAssociations(hospitaisVinculados);
        } catch (error) {
          console.error('Erro ao carregar hospitais vinculados:', error);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar hospitais:', error);
    } finally {
      setLoadingHospitais(false);
    }
  }, [clienteId, representante]);

  useEffect(() => {
    fetchHospitais();
  }, [fetchHospitais]);

  // Filtered lists for dual-list UI
  const filteredAvailable = useMemo(() => {
    const assignedIds = new Set(associations.map((assoc) => assoc.hospital_id));
    const normalizedSearch = searchLeft.trim().toLowerCase();
    return availableHospitais
      .filter((hospital) => !assignedIds.has(hospital.id))
      .filter((hospital) => {
        if (!normalizedSearch) return true;
        const haystack = `${hospital.nome_fantasia ?? ''} ${hospital.codigo ?? ''} ${hospital.cidade ?? ''} ${hospital.estado ?? ''}`.toLowerCase();
        // Story 2.38: busca por CNPJ normalizada (ignora ./- /)
        return haystack.includes(normalizedSearch) || cnpjMatch((hospital as any).cnpj, searchLeft);
      })
      .sort((a, b) => (a.nome_fantasia || '').localeCompare(b.nome_fantasia || ''));
  }, [associations, availableHospitais, searchLeft]);

  const filteredLinked = useMemo(() => {
    const normalizedSearch = searchRight.trim().toLowerCase();
    if (!normalizedSearch) return associations;
    return associations.filter((assoc) => {
      const haystack = `${assoc.hospital_nome ?? ''}`.toLowerCase();
      // Story 2.38: busca por CNPJ normalizada (campo opcional hospital_cnpj)
      return haystack.includes(normalizedSearch) || cnpjMatch((assoc as any).hospital_cnpj, searchRight);
    });
  }, [associations, searchRight]);

  // Selection handlers
  const toggleAvailableSelection = (id: number) => {
    setSelectedAvailable(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleLinkedSelection = (id: number) => {
    setSelectedLinked(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleAllAvailable = () => {
    if (selectedAvailable.length === filteredAvailable.length) {
      setSelectedAvailable([]);
    } else {
      setSelectedAvailable(filteredAvailable.map(h => h.id));
    }
  };

  const toggleAllLinked = () => {
    if (selectedLinked.length === filteredLinked.length) {
      setSelectedLinked([]);
    } else {
      setSelectedLinked(filteredLinked.map(a => a.hospital_id));
    }
  };

  // Transfer handlers
  const handleLinkSelected = async () => {
    if (selectedAvailable.length === 0 || !representante) return;
    try {
      setLinking(true);
      await Promise.all(
        selectedAvailable.map(hospitalId => representantesService.addHospital(representante.id, hospitalId))
      );
      await fetchHospitais();
      setSelectedAvailable([]);
      msg.success(`${selectedAvailable.length} hospital(is) vinculado(s) com sucesso!`);
    } catch (error: any) {
      msg.error(error?.response?.data?.detail || 'Erro ao vincular hospitais');
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkSelected = async () => {
    if (selectedLinked.length === 0 || !representante) return;
    const confirmed = await confirm({
      title: 'Desvincular Hospitais',
      message: `Deseja realmente desvincular ${selectedLinked.length} hospital(is) deste representante?`,
      variant: 'danger',
      confirmText: 'Desvincular',
    });
    if (!confirmed) return;
    try {
      setUnlinking(true);
      await Promise.all(
        selectedLinked.map(hospitalId => representantesService.removeHospital(representante.id, hospitalId))
      );
      setAssociations(prev => prev.filter(assoc => !selectedLinked.includes(assoc.hospital_id)));
      setSelectedLinked([]);
      msg.success(`${selectedLinked.length} hospital(is) desvinculado(s) com sucesso!`);
    } catch (error: any) {
      msg.error(error?.response?.data?.detail || 'Erro ao desvincular hospitais');
    } finally {
      setUnlinking(false);
    }
  };

  const handleLinkAll = async () => {
    if (filteredAvailable.length === 0 || !representante) return;
    const confirmed = await confirm({
      title: 'Vincular Todos',
      message: `Deseja vincular todos os ${filteredAvailable.length} hospitais visíveis?`,
      confirmText: 'Vincular Todos',
    });
    if (!confirmed) return;
    try {
      setLinking(true);
      await Promise.all(
        filteredAvailable.map(hospital => representantesService.addHospital(representante.id, hospital.id))
      );
      await fetchHospitais();
      setSelectedAvailable([]);
      msg.success(`${filteredAvailable.length} hospital(is) vinculado(s) com sucesso!`);
    } catch (error: any) {
      msg.error(error?.response?.data?.detail || 'Erro ao vincular todos os hospitais');
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkAll = async () => {
    if (filteredLinked.length === 0 || !representante) return;
    const confirmed = await confirm({
      title: 'Desvincular Todos',
      message: `Deseja realmente desvincular TODOS os ${filteredLinked.length} hospitais visíveis?`,
      variant: 'danger',
      confirmText: 'Desvincular Todos',
    });
    if (!confirmed) return;
    try {
      setUnlinking(true);
      await Promise.all(
        filteredLinked.map(assoc => representantesService.removeHospital(representante.id, assoc.hospital_id))
      );
      setAssociations(prev => prev.filter(assoc => !filteredLinked.some(f => f.hospital_id === assoc.hospital_id)));
      setSelectedLinked([]);
      msg.success(`${filteredLinked.length} hospital(is) desvinculado(s) com sucesso!`);
    } catch (error: any) {
      msg.error(error?.response?.data?.detail || 'Erro ao desvincular todos os hospitais');
    } finally {
      setUnlinking(false);
    }
  };

  // Field change handlers with masks
  const handleCpfCnpjChange = (value: string) => {
    const numbers = limparDocumento(value).substring(0, 14);
    const formatted = numbers.length <= 11 ? mascaraCPF(numbers) : mascaraCNPJ(numbers);
    setFormData({ ...formData, cpf_cnpj: formatted });
    if (errors.cpf_cnpj) setErrors({ ...errors, cpf_cnpj: '' });
  };

  const handleTelefoneChange = (value: string) => {
    const formatted = mascaraTelefone(value);
    setFormData({ ...formData, telefone: formatted });
    if (errors.telefone) setErrors({ ...errors, telefone: '' });
  };

  // Validation
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    }
    if (formData.cpf_cnpj && formData.cpf_cnpj.trim()) {
      const cleanCpfCnpj = limparDocumento(formData.cpf_cnpj);
      if (cleanCpfCnpj.length === 11 && !validarCPF(formData.cpf_cnpj)) {
        newErrors.cpf_cnpj = 'CPF inválido. Verifique os números digitados.';
      } else if (cleanCpfCnpj.length === 14 && !validarCNPJ(formData.cpf_cnpj)) {
        newErrors.cpf_cnpj = 'CNPJ inválido. Verifique os números digitados.';
      }
    }
    if (!formData.telefone.trim()) {
      newErrors.telefone = 'Telefone é obrigatório';
    } else if (!validarTelefone(formData.telefone)) {
      newErrors.telefone = 'Telefone inválido. Use (XX) XXXXX-XXXX ou (XX) XXXX-XXXX';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email é obrigatório (identificador único)';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Email inválido';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      setLoading(true);
      if (representante) {
        await representantesService.update(representante.id, formData);
      } else {
        const dataToSend = clienteId ? { ...formData, cliente_id: clienteId } : formData;
        await representantesService.create(dataToSend as any);
      }
      onSave();
    } catch (error: any) {
      let errorMessage = 'Erro ao salvar representante';
      if (error.response?.data) {
        const data = error.response.data;
        if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        } else if (Array.isArray(data.detail)) {
          errorMessage = data.detail.map((e: any) => e.msg || JSON.stringify(e)).join(', ');
        } else if (typeof data.detail === 'object') {
          errorMessage = JSON.stringify(data.detail);
        } else if (data.message) {
          errorMessage = data.message;
        }
      }
      if (errorMessage.includes('Código') || errorMessage.includes('codigo') || errorMessage.includes('cpf_cnpj')) {
        setErrors({ cpf_cnpj: errorMessage });
      } else if (errorMessage.includes('Email')) {
        setErrors({ email: errorMessage });
      } else {
        msg.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (value?: string) => {
    if (!value) return 'Sem data de início';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString('pt-BR');
  };

  return {
    formData,
    setFormData,
    loading,
    errors,
    availableHospitais,
    associations,
    loadingHospitais,
    selectedAvailable,
    selectedLinked,
    searchLeft,
    setSearchLeft,
    searchRight,
    setSearchRight,
    linking,
    unlinking,
    filteredAvailable,
    filteredLinked,
    ConfirmDialog,
    handleCpfCnpjChange,
    handleTelefoneChange,
    handleLinkSelected,
    handleUnlinkSelected,
    handleLinkAll,
    handleUnlinkAll,
    toggleAvailableSelection,
    toggleLinkedSelection,
    toggleAllAvailable,
    toggleAllLinked,
    handleSubmit,
    formatDate,
  };
}
